/**
 * Colour helpers for the member home's marks: WCAG luminance and contrast, and the AA-safe mark
 * colour. Pure, so the home model (here) and the web components (`@teamna/ui`) use one rule.
 */

function channels(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((part) => Math.round(part).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

/** WCAG relative luminance of a `#rrggbb` colour. */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((part) => {
    const c = part / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio of two `#rrggbb` colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

/**
 * A participant's mark colour on the member home: the group colour, made darker until white text on
 * it passes WCAG AA (4.5:1). The week strip rings, the participant chips and the sessions left rows
 * all use it, so one person is one colour, and the colour is never the only thing a mark says (the
 * ring is solid or dashed, the chip and the row carry the name). A dark group colour comes back as it
 * is. Takes a `#rrggbb` colour; the apps resolve a missing group colour first.
 */
export function markColor(hex: string): string {
  const base = channels(hex);
  for (let keep = 1; keep > 0; keep -= 0.05) {
    const candidate = toHex([base[0] * keep, base[1] * keep, base[2] * keep]);
    if (contrastRatio(candidate, "#FFFFFF") >= 4.5) return candidate;
  }
  return "#000000";
}
